/** Responsibility: Validate core game state integrity (world seed, clock, room, player location, meta, history).
 *  Scope: Supervisor check only; reads state, never mutates. */
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"

export const stateCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []

  if (!state.world?.seed?.trim()) {
    incidents.push({
      code: 'state_world_seed_missing',
      domain: 'persistence',
      severity: 'high',
      confidence: 0.99,
      message: 'World seed is empty or missing.',
      remediationPlan: 'Set a fallback world seed.',
    })
  }

  const clock = state.world?.clock
  if (!clock || typeof clock.hour !== 'number' || typeof clock.day !== 'number') {
    incidents.push({
      code: 'state_clock_invalid',
      domain: 'persistence',
      severity: 'high',
      confidence: 0.98,
      message: 'World clock is missing or has invalid fields.',
      remediationPlan: 'Reset clock to default values.',
    })
  } else {
    if (clock.hour < 0 || clock.hour > 23) {
      incidents.push({
        code: 'state_clock_hour_out_of_range',
        domain: 'persistence',
        severity: 'medium',
        confidence: 0.95,
        message: `Clock hour is ${clock.hour}, expected 0-23.`,
        remediationPlan: 'Clamp clock hour to 0-23.',
      })
    }
  }

  if (!state.currentRoom?.name?.trim()) {
    incidents.push({
      code: 'state_current_room_missing_name',
      domain: 'persistence',
      severity: 'high',
      confidence: 0.99,
      message: 'Current room has no name.',
      remediationPlan: 'Set a fallback room name from atlas or default.',
    })
  }

  const loc = state.player?.location
  if (!loc || typeof loc.x !== 'number' || typeof loc.y !== 'number') {
    incidents.push({
      code: 'state_player_location_invalid',
      domain: 'actor',
      severity: 'high',
      confidence: 0.99,
      message: 'Player location is missing or invalid.',
      remediationPlan: 'Reset player location to 0,0.',
    })
  }

  if (!state.meta?.name?.trim()) {
    incidents.push({
      code: 'state_meta_name_missing',
      domain: 'persistence',
      severity: 'medium',
      confidence: 0.95,
      message: 'Game save meta name is empty.',
      remediationPlan: 'Set meta name from player identity.',
    })
  }

  const histLen = Array.isArray(state.history) ? state.history.length : -1
  if (histLen < 0) {
    incidents.push({
      code: 'state_history_not_array',
      domain: 'persistence',
      severity: 'medium',
      confidence: 0.99,
      message: 'History is not an array.',
      remediationPlan: 'Reset history to empty array.',
    })
  }

  return { passed: incidents.length === 0, incidents }
}
